import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import AppIcon from '@/components/generals/app-icon'
import CtaLink from '@/components/generals/cta-link'
import { appLinks } from '@/constants/site'

// Small sticky download bar for phones. It appears once the hero has scrolled away
// and steps aside again while the closing call to action or the footer is on screen.
const MobileCtaBar: FC = (): ReactNode => {
    const [visible, setVisible] = useState(false)

    useEffect(() => {
        const hero = document.getElementById('top')
        const endings = ['download', 'site-footer']
            .map((id) => document.getElementById(id))
            .filter((element): element is HTMLElement => element !== null)

        if (!hero) return

        const state = new Map<Element, boolean>()

        const observer = new IntersectionObserver((entries) => {
            for (const entry of entries) state.set(entry.target, entry.isIntersecting)

            const heroVisible = state.get(hero) ?? true
            const endingVisible = endings.some((element) => state.get(element))

            setVisible(!heroVisible && !endingVisible)
        })

        observer.observe(hero)
        endings.forEach((element) => observer.observe(element))

        return () => observer.disconnect()
    }, [])

    return (
        <div
            inert={!visible}
            className={cn(
                'fixed inset-x-3 bottom-[calc(0.75rem+env(safe-area-inset-bottom))] z-40 transition-all duration-300 md:hidden',
                visible ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-[140%] opacity-0'
            )}
        >
            <div className='flex items-center gap-3 rounded-2xl border border-border bg-card/95 p-2.5 shadow-[0_18px_40px_-16px_rgb(2_6_23/0.45)] backdrop-blur-xl'>
                <AppIcon className='size-10' />

                <div className='min-w-0 flex-1 leading-tight'>
                    <p className='font-heading text-sm font-bold text-foreground'>
                        MultiScreen
                    </p>

                    <p className='truncate text-xs text-muted-foreground'>
                        1, 2 and 4 views free
                    </p>
                </div>

                <CtaLink href={appLinks.playStore} external size='sm'>
                    Google Play
                </CtaLink>
            </div>
        </div>
    )
}

export default MobileCtaBar
